import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';

export interface LeaderboardEntry {
  playerName: string;
  playerRut: string;
  score: number;
  gameId: string;
  date: string;
  isHigherBetter: boolean;
}

export interface GameScore {
  gameId: string;
  score: number;
  isHigherBetter: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class LeaderboardService {
  private readonly STORAGE_KEY = 'kiosk_leaderboard';
  private readonly MAX_ENTRIES_PER_GAME = 10;

  private leaderboard$ = new BehaviorSubject<LeaderboardEntry[]>(this.loadAll());
  private clientCode = '';

  constructor(private http: HttpClient) {
    const saved = localStorage.getItem('kiosk_client_code');
    if (saved) this.clientCode = saved;
  }

  setClientCode(code: string): void {
    this.clientCode = code;
    localStorage.setItem('kiosk_client_code', code);
  }

  getClientCode(): string {
    return this.clientCode;
  }

  getLeaderboard$() {
    return this.leaderboard$.asObservable();
  }

  getTopScores(gameId: string, limit = 5): LeaderboardEntry[] {
    const all = this.loadAll().filter(e => e.gameId === gameId);
    const first = all[0];
    if (!first) return [];
    if (first.isHigherBetter) {
      all.sort((a, b) => b.score - a.score);
    } else {
      all.sort((a, b) => a.score - b.score);
    }
    return all.slice(0, limit);
  }

  getBestScore(gameId: string): LeaderboardEntry | null {
    const top = this.getTopScores(gameId, 1);
    return top.length ? top[0] : null;
  }

  isNewRecord(gameScore: GameScore): boolean {
    const best = this.getBestScore(gameScore.gameId);
    if (!best) return true;
    if (gameScore.isHigherBetter) {
      return gameScore.score > best.score;
    } else {
      return gameScore.score < best.score;
    }
  }

  isTopScore(gameScore: GameScore): boolean {
    const all = this.loadAll().filter(e => e.gameId === gameScore.gameId);
    if (all.length === 0) return true;
    if (gameScore.isHigherBetter) {
      all.sort((a, b) => b.score - a.score);
    } else {
      all.sort((a, b) => a.score - b.score);
    }
    const worst = all[all.length - 1];
    if (all.length < this.MAX_ENTRIES_PER_GAME) {
      if (gameScore.isHigherBetter) {
        return gameScore.score >= worst.score;
      } else {
        return gameScore.score <= worst.score;
      }
    }
    if (gameScore.isHigherBetter) {
      return gameScore.score > worst.score;
    } else {
      return gameScore.score < worst.score;
    }
  }

  addEntry(entry: LeaderboardEntry): void {
    const all = this.loadAll();
    all.push(entry);

    // Keep only top N per game
    const byGame = new Map<string, LeaderboardEntry[]>();
    for (const e of all) {
      if (!byGame.has(e.gameId)) byGame.set(e.gameId, []);
      byGame.get(e.gameId)!.push(e);
    }

    const trimmed: LeaderboardEntry[] = [];
    byGame.forEach((entries, gameId) => {
      const first = entries[0];
      if (first.isHigherBetter) {
        entries.sort((a, b) => b.score - a.score);
      } else {
        entries.sort((a, b) => a.score - b.score);
      }
      trimmed.push(...entries.slice(0, this.MAX_ENTRIES_PER_GAME));
    });

    this.saveAll(trimmed);
    this.leaderboard$.next(trimmed);

    this.syncEntryToApi(entry);
  }

  private syncEntryToApi(entry: LeaderboardEntry): void {
    if (!this.clientCode) return;
    const payload = {
      client_code: this.clientCode,
      game_id: entry.gameId,
      player_name: entry.playerName,
      player_rut: entry.playerRut,
      score: entry.score,
      is_higher_better: entry.isHigherBetter,
    };
    this.http.post(`${environment.apiUrl}/leaderboard/`, payload).subscribe({
      error: (err) => console.warn('Leaderboard API sync failed:', err.message),
    });
  }

  clearGame(gameId: string): void {
    const all = this.loadAll().filter(e => e.gameId !== gameId);
    this.saveAll(all);
    this.leaderboard$.next(all);
  }

  clearAll(): void {
    this.saveAll([]);
    this.leaderboard$.next([]);
  }

  private loadAll(): LeaderboardEntry[] {
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  private saveAll(entries: LeaderboardEntry[]): void {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(entries));
  }

  formatScore(gameId: string, score: number): string {
    switch (gameId) {
      case 'memory':
        return this.formatTime(score);
      case 'puzzle':
        return `${score} movs`;
      case 'reaction':
        return `${score}ms`;
      case 'simon':
        return `Nivel ${score}`;
      case 'whack':
      case 'speedtap':
      case 'balloonpop':
        return `${score} pts`;
      default:
        return `${score}`;
    }
  }

  private formatTime(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }
}
